import { 
	TypeAheadUsers,
	PhoenixUser,
	PendingUser,
	UserStatistic
} from 'phoenix/domain/user';
import { CommonUtilsService } from 'util/services/commonUtilsService';
import { PhoenixToggleService } from 'phoenix/services/phoenixToggleService';

/**
* @ngdoc service
* @name  sharebase.sbUsersService
* 
* @description 
* A service used to retrieve ShareBase users
**/
export default class UsersService{
	public static $inject = [
		'$http',
		'utils',
		'pxUriPathService',
		'pxPhoenixToggleService'
	];

	private sharingEndpointsPromise:ng.IPromise<any>;
	constructor(
		private $http:ng.IHttpService, 
		private utils:CommonUtilsService, 
		private pxUriPathService, 
		private pxPhoenixToggleService:PhoenixToggleService
	) {
	}

	public getSharingEndpoints(systemName?: string){
		if(this.sharingEndpointsPromise){
			return this.sharingEndpointsPromise;
		}

		let opts: any = { };
		if (systemName) {
			opts.system = systemName;
		}

		this.sharingEndpointsPromise = this.pxPhoenixToggleService.isEnabled('system-sharing-endpoint')
			.then(isEnabled => {
				let path = isEnabled ? '/{system}/sharing' : '/sharing';
				let uri = this.pxUriPathService.resolvePath(path);

				return this.$http.get(uri)
					.then(response => response.data);
			});

		return this.sharingEndpointsPromise;
	}

	/**
	* @ngdoc method
	* @name sbUsersService#getUsers
	* @methodOf sharebase.sbUsersService
	* @kind function
	*
	* @description
	* Gets a collection of users
	*
	* @returns {Deferred} A promise that will resolve with a collection of users
	* 
	*/
	public getUsers(systemName:string, showCoreStats:boolean) {
		let uri = this.pxUriPathService.resolvePath('/{system}/users{?showCoreStatistics}', {
			system: systemName,
			showCoreStatistics:showCoreStats
		});

		return this.$http.get(uri)
			.then(response => response.data as PhoenixUser[]);
	}

	public getDocumentsStoredByUser(systemName:string, includeStatlessUsers:boolean = true){
		let uri = this.pxUriPathService.resolvePath('/{system}/statistics/Core/DocumentsStoredByUser/{?addUserDefaults}', {
			system: systemName,
			addUserDefaults: includeStatlessUsers
		});

		return this.$http.get<UserStatistic[]>(uri).then(resp => resp.data);
	}

	public getDiskUsageByUser(systemName:string, includeStatlessUsers:boolean = true){
		let uri = this.pxUriPathService.resolvePath('/{system}/statistics/Core/TotalDiskUsage/{?addUserDefaults}', {
			system: systemName,
			addUserDefaults: includeStatlessUsers
		});

		return this.$http.get<UserStatistic[]>(uri).then(resp => resp.data);
	}

	/**
	* @ngdoc method
	* @name sbUsersService#getUsersTypeAhead
	* @methodOf sharebase.sbUsersService
	* @kind function
	*
	* @description
	* Gets a collection of users
	*
	* @returns {Deferred} A promise that will resolve with a collection of users
	* 
	*/
	public getUsersTypeAhead = this.utils.debounce(this._getUsersTypeAhead, 250, this);
	private _getUsersTypeAhead(filter?:string, systemName?: string){
		return this.getSharingEndpoints(systemName)
			.then(sharingEndpoints => {
				let uri = sharingEndpoints.Links.UsersAndGroupsSuggestionsUri;
				uri = this.pxUriPathService.resolvePath(uri, {
					filter,
					includeGroups: false,
					includeUsers: true
				});

				return this.$http.get(uri);
			})
			.then(response => (response.data as TypeAheadUsers).Users);
	}

	/**
	* @ngdoc method
	* @name sbUsersService#getPendingUsers
	* @methodOf sharebase.sbUsersService
	* @kind function
	*
	* @description
	* Gets a collection of invited users
	*
	* @returns {Deferred} A promise that will resolve with a collection of invited users
	* 
	*/
	public getPendingUsers() {
		let uri = this.pxUriPathService.resolvePath('/users/invites');
		return this.$http.get(uri)
			.then(response => response.data as PendingUser[]);
	}

	/**
	* @ngdoc method
	* @name sbUsersService#inviteUsers
	* @methodOf sharebase.sbUsersService
	* @kind function
	*
	* @description
	* Submits a collection of new invited users
	*
	* @returns {Deferred} A promise of the status of the submission
	* 
	*/
	public inviteUsers(systemName:string, emails, roles, groups) {
		var uri = this.pxUriPathService.resolvePath('/{system}/users/registration', {
			system: systemName
		});

		return this.$http.post(uri, { 
				EmailAddresses: emails,
				Roles: roles,
				UserGroupIds: groups 
			})
			.then(response => response.data);
	}

	public resendInvitation(pendingUser:PendingUser) {
		return this.$http
			.post(pendingUser.Links.ResendInviteUri, null)
			.then(res => res.data);
	}
	
	public changePendingUserEmail(pendingUser:PendingUser, newEmail) {
		return this.$http
			.patch(pendingUser.Links.SendInviteToNewEmailUri, newEmail)
			.then(res => res.data as PendingUser);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/services/usersService.ts